/** Shop top promotions — product campaigns + optional free-delivery promo. */
import { discountMadeItFree, discountPercent, effectiveVariantPrice as variantOwnPrice } from './productPrice';
import { currencySymbol, shopAmount } from './currency';
import { ownerText } from './shopFeatures';
import { isAskProduct } from './orderMode';
import { variantPurchaseCap } from './stockCap';

export const SHOP_PROMOTION_TARGETS = new Set(['PRODUCTS', 'PRODUCT_CATEGORIES', 'PRODUCT_COLLECTIONS', 'MIXED', 'ALL']);

/**
 * Target types that may discount a PRODUCT's price.
 *
 * ── This is the same rule twice, and it is a known hazard ─────────────────
 * The discount a customer is CHARGED is computed by the server
 * (campaigns.service.ts, applyCampaigns). The discount a customer is SHOWN is
 * computed here. Two implementations of one money rule agree only until one of
 * them is edited — and they had drifted four ways at once: this took the best
 * single discount where the server stacks by priority, read only a campaign's
 * end date, priced a unit with campaigns that wait for a basket, and knew
 * nothing about collections.
 *
 * So the two are no longer kept together by a comment. ONE table of baskets,
 * `stylon-server/src/modules/campaigns/campaign-pricing.parity.fixtures.json`,
 * is asserted against BOTH implementations — `campaign-pricing.parity.spec.ts`
 * on the server, `utils/__tests__/campaignPricingParity.test.js` here. Change
 * the rule on either side and one of them fails.
 *
 * The rule, mirrored from the server (owner decision 2026-09-13, DISC-9 and
 * DISC-13), decided LINE BY LINE so the card and the basket cannot disagree:
 *   1. a campaign is in play when it is ACTIVE (or SCHEDULED with a start), its
 *      booking window holds TODAY IN THE SHOP'S OWN DAY (inclusive; the server
 *      publishes `timeZone` and `todayKey` on every campaign), it has no
 *      appointment dates or weekdays (a product is not an appointment), the
 *      shopper has not used it up (`remainingUses`), and its minimum spend is
 *      met — by the basket; by a card only when ONE unit of it already meets
 *      it, because that is what checkout charges for one;
 *   2. it covers a line when its targets reach it. ALL covers everything.
 *      PRODUCTS / PRODUCT_CATEGORIES / MIXED that name nothing cover every
 *      product; PRODUCT_COLLECTIONS that names nothing covers NOTHING;
 *      SERVICES / PACKAGES never reach one;
 *   3. PERCENT comes off every unit, rounded to the agora. A FIXED amount aimed
 *      at a product or option comes off EVERY UNIT; on the whole shop, a
 *      category or a collection it comes off ONCE per basket, shared over its
 *      lines by value; MIXED does the first for product/option targets and the
 *      second for the rest;
 *   4. each line gets the BIGGER of the best single "Only one" (NONE) discount
 *      and all its "Allow stacking" (STACK) discounts added up, never more than
 *      the line. Priority, then the older campaign, only settles a tie. A
 *      campaign that took nothing off is not named; a free-delivery-only one
 *      never takes part.
 *
 * The desk has a verbatim copy of this region (salon portal,
 * `src/utils/shopPromotions.js`), guarded character-for-character by
 * `shopPromotionsParity.test.js` there.
 */
export const PRODUCT_CAMPAIGN_TARGETS = new Set(['PRODUCTS', 'PRODUCT_CATEGORIES', 'PRODUCT_COLLECTIONS', 'MIXED', 'ALL']);

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** A date's UTC calendar day as `YYYY-MM-DD` — how a stored window bound names its day. */
function utcDayKey(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d.toISOString().slice(0, 10) : null;
}

/**
 * Today in the SHOP's own calendar, as the server reads it: in the campaign's
 * `timeZone` when it carries one, else the `todayKey` the server published with
 * it, else the UTC day.
 */
function shopTodayKey(campaign) {
  // Listed as live on a past day (the desk editing an order placed then): that day.
  if (campaign && typeof campaign.liveOn === 'string' && DAY_KEY.test(campaign.liveOn)) return campaign.liveOn;
  const zone = campaign && campaign.timeZone;
  if (zone) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(new Date());
      const part = (type) => (parts.find((p) => p.type === type) || {}).value;
      const key = `${part('year')}-${part('month')}-${part('day')}`;
      if (DAY_KEY.test(key)) return key;
    } catch (_) { /* a zone this browser does not know: fall through */ }
  }
  if (campaign && typeof campaign.todayKey === 'string' && DAY_KEY.test(campaign.todayKey)) return campaign.todayKey;
  return utcDayKey(new Date());
}

/** Both ends inclusive, by the shop's calendar day, as `dayInWindow` on the server. */
function campaignBookingDateInRange(campaign) {
  const start = utcDayKey(campaign.bookingDateStart ?? campaign.booking_date_start);
  const end = utcDayKey(campaign.bookingDateEnd ?? campaign.booking_date_end);
  const today = shopTodayKey(campaign);
  if (start && today < start) return false;
  if (end && today > end) return false;
  return true;
}

/** An appointment window or weekday list: the server skips these for a basket with no appointment. */
function campaignNeedsAnAppointment(campaign) {
  const days = campaign.appointmentDaysOfWeek;
  return !!(campaign.appointmentDateStart
    || campaign.appointmentDateEnd
    || (Array.isArray(days) && days.length > 0));
}

/** A minimum spend in currency units, or 0 for none. A basket meets it with its subtotal, a card with one unit. */
function campaignMinimumSpend(campaign) {
  const minSpend = Number(campaign.minSpend ?? campaign.minimumSpend);
  return Number.isFinite(minSpend) && minSpend > 0 ? minSpend : 0;
}

/** A promotion whose whole effect is free delivery: it takes nothing off a line (`isFreeDeliveryOnly` on the server). */
function campaignIsFreeDeliveryOnly(c) {
  return Number(c.discountValue) === 0 && c.freeDeliveryThreshold != null;
}

/**
 * In play for this shopper today: ACTIVE (or SCHEDULED with a start), in its
 * window, not used up, and — unless the caller has already matched it to an
 * appointment's date (`withAppointments`) — not an appointment promotion. A
 * free-delivery offer passes the same gates as a discount.
 */
function campaignIsInPlay(c, withAppointments = false) {
  if (!c) return false;
  const scheduledWithStart = c.status === 'SCHEDULED' && !!(c.bookingDateStart ?? c.booking_date_start);
  // A campaign the server listed as live on a past day (`liveOn`) may have ENDED since.
  const endedSinceThatDay = c.status === 'ENDED' && typeof c.liveOn === 'string';
  if (c.status && c.status !== 'ACTIVE' && !scheduledWithStart && !endedSinceThatDay) return false;
  if (!campaignBookingDateInRange(c)) return false;
  if (!withAppointments && campaignNeedsAnAppointment(c)) return false;
  return !(c.remainingUses != null && Number(c.remainingUses) <= 0);
}

/** In play (`campaignIsInPlay`) as a DISCOUNT: a free-delivery-only offer never takes part in a price. */
function campaignIsLive(c, withAppointments = false) {
  return campaignIsInPlay(c, withAppointments) && !campaignIsFreeDeliveryOnly(c);
}

/**
 * Does a campaign cover this product line, and how? `null` when it does not;
 * `{ perUnit: true }` when a FIXED amount comes off every unit (a PRODUCT or
 * PRODUCT_VARIANT target), `{ perUnit: false }` when it comes off once per basket.
 *
 * A product sold by ASKING the shop (`orderMode: 'ask'`) is in no campaign (final review M6): it is never sold by cart, so
 * the server never takes anything off it, and a "-20%" or a struck price on a tray the shopper can only ask about is a
 * promise nobody made. Every display door - the card, the strips, the page, the order offers - prices through here.
 */
function campaignAppliesToProduct(c, product, variant) {
  if (isAskProduct(product)) return null;
  const tt = c.targetType;
  if (tt === 'ALL') return { perUnit: false };
  const targets = c.targets || [];
  const ids = (type) => new Set(targets.filter((tg) => tg.itemType === type).map((tg) => tg.itemId));
  const productIds = ids('PRODUCT');
  const categoryIds = ids('PRODUCT_CATEGORY');
  const variantIds = ids('PRODUCT_VARIANT');
  const collectionTargets = targets.filter((tg) => tg.itemType === 'PRODUCT_COLLECTION');
  const productId = product?.id;
  const categoryId = product?.categoryId || null;
  const inCategory = !!categoryId && categoryIds.has(categoryId);
  // What each named collection resolves to right now, as `findActiveForStore`
  // publishes it. No `productIds` means nothing is known to be in it, which is
  // the server's own reading of a deleted collection: it matches nothing.
  const inCollection = collectionTargets.some(
    (tg) => Array.isArray(tg.productIds) && tg.productIds.includes(productId),
  );
  const byProductTarget = (!!productId && productIds.has(productId)) || (!!variant && variantIds.has(variant.id));
  const onceOff = { perUnit: false };
  if (tt === 'PRODUCTS') {
    if (productIds.size > 0 || variantIds.size > 0) return byProductTarget ? { perUnit: true } : null;
    return targets.length === 0 ? onceOff : null;
  }
  if (tt === 'PRODUCT_CATEGORIES') {
    if (categoryIds.size > 0) return inCategory ? onceOff : null;
    return targets.length === 0 ? onceOff : null;
  }
  if (tt === 'PRODUCT_COLLECTIONS') {
    return collectionTargets.length > 0 && inCollection ? onceOff : null;
  }
  if (tt === 'MIXED') {
    if (targets.length === 0) return onceOff;
    if (byProductTarget) return { perUnit: true };
    return inCategory || inCollection ? onceOff : null;
  }
  // SERVICES / PACKAGES, or a type this storefront has never heard of.
  return null;
}

/**
 * Does a campaign cover this SERVICE or PACKAGE line? `null` or `{ perUnit: false }`
 * (a fixed amount on a booking line comes off once per booking), as
 * `linesCoveredBy` on the server: ALL covers it; SERVICES / PACKAGES cover the
 * lines they name, or every line of their kind when they name nothing; MIXED
 * covers what it names, or everything when it names nothing.
 */
function campaignAppliesToBookingItem(c, kind, itemId) {
  const tt = c.targetType;
  const onceOff = { perUnit: false };
  if (tt === 'ALL') return onceOff;
  const targets = c.targets || [];
  const named = targets.filter((tg) => tg.itemType === kind).map((tg) => String(tg.itemId));
  const covered = named.includes(String(itemId));
  if ((kind === 'SERVICE' && tt === 'SERVICES') || (kind === 'PACKAGE' && tt === 'PACKAGES')) {
    if (named.length > 0) return covered ? onceOff : null;
    return targets.length === 0 ? onceOff : null;
  }
  if (tt === 'MIXED') return targets.length === 0 || covered ? onceOff : null;
  return null;
}

/** Priority first, then the oldest — the order a tie is settled in on the server. */
function byPriorityThenAge(a, b) {
  const pa = Number(a.campaign.priority) || 0;
  const pb = Number(b.campaign.priority) || 0;
  if (pa !== pb) return pb - pa;
  const ta = Date.parse(a.campaign.createdAt);
  const tb = Date.parse(b.campaign.createdAt);
  if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
  return a.index - b.index;
}

/** A FIXED amount that comes off once per order on this line (not unit by unit). */
function isOrderAmount(campaign, how) {
  return campaign.discountType !== 'PERCENT' && !how.perUnit;
}

/** Whole agorot, half up; the epsilon absorbs binary noise such as 7.4849999999. */
function toAgorot(n) {
  return Math.round((Number(n) || 0) * 100 + 1e-7);
}

/**
 * WHOLE SHEKELS ON A DISCOUNTED PRICE — the business's choice (DISC-34),
 * `discount-price-rounding.ts` on the server.
 *
 * A 15% sale on a ₪579 jacket charges ₪492.15, and the business's own ads say
 * ₪492. The agorot are arithmetic, not an offer, so a business may round the
 * price a discount leaves: `off` (as it has always been, the DEFAULT, so no
 * other business moves), `down` (492.15 → 492, always in the shopper's favour)
 * or `nearest` (492.15 → 492, and 976.65 → 977).
 *
 * The rounding happens ONCE, inside `combineCampaignShares`, on the discount
 * itself — never on a price about to be printed — so the card, the catalogue
 * feed, the charge and this desk all read one number, and an order's total is
 * still the sum of its lines.
 *
 * What is NOT rounded: a fixed amount off the whole ORDER (the shop, a
 * category, a collection). "₪50 off your order" is a promise about the order,
 * shared over its lines by value; rounding it would quietly make it ₪49.40.
 * Only the part of a line's discount that comes off unit by unit — a
 * percentage, or a fixed amount per unit — is rounded, and the fixed amount off
 * the order then comes off the rounded price.
 */
export const DISCOUNT_PRICE_ROUNDINGS = ['off', 'down', 'nearest'];
export const DEFAULT_DISCOUNT_PRICE_ROUNDING = 'off';

/** The shop's rounding as the payload carries it — the first campaign that names one; today's behaviour otherwise. */
export function priceRoundingOf(campaigns) {
  for (const c of campaigns || []) {
    const named = String((c && c.priceRounding) ?? '');
    if (DISCOUNT_PRICE_ROUNDINGS.includes(named)) return named;
  }
  return DEFAULT_DISCOUNT_PRICE_ROUNDING;
}

/**
 * `paidAgorot` — what one unit costs once its per-unit discounts came off — on
 * a whole currency unit, never above `unitAgorot` (the rounding may not charge
 * more than the shelf) and never below nothing.
 */
function roundedUnitAgorot(paidAgorot, unitAgorot, rounding) {
  if (rounding === 'off') return paidAgorot;
  const whole = rounding === 'down' ? Math.floor(paidAgorot / 100) * 100 : Math.round(paidAgorot / 100) * 100;
  return Math.max(0, Math.min(unitAgorot, whole));
}

/** `amount` agorot shared over `weights` in proportion; the odd agorot go to the largest remainders, earlier line first. */
function shareOut(amount, weights) {
  const total = weights.reduce((s, w) => s + w, 0);
  if (amount <= 0 || total <= 0) return weights.map(() => 0);
  const shares = weights.map((w) => Math.floor((amount * w) / total));
  const remainders = weights.map((w, i) => ({ i, r: (amount * w) % total }));
  let left = amount - shares.reduce((s, v) => s + v, 0);
  remainders.sort((a, b) => b.r - a.r || a.i - b.i);
  remainders.forEach(({ i }) => {
    if (left <= 0) return;
    shares[i] += 1;
    left -= 1;
  });
  return shares;
}

/**
 * THE BETTER DEAL WINS, LINE BY LINE — `combineCampaignShares` on the server.
 * `lines` carry `unitAgorot`, `quantity`, `lineAgorot`; `candidates` arrive in
 * tie-break order. Returns agorot per line, per candidate, and which lines the
 * stack won.
 *
 * `rounding` is the business's rounding of a discounted price
 * (`DISCOUNT_PRICE_ROUNDINGS`): the part of a line that came off UNIT BY UNIT
 * is moved so the paid unit lands on a whole currency unit. A fixed amount off
 * the whole order is never rounded.
 */
function combineCampaignShares(lines, candidates, rounding = DEFAULT_DISCOUNT_PRICE_ROUNDING) {
  // Whether a candidate reaches a line unit by unit: every PERCENT does, and a
  // FIXED amount only where its cover says so. Only these parts are rounded.
  const perUnitAt = candidates.map(({ campaign, covers }) => lines.map((_, li) => (
    campaign.discountType === 'PERCENT' ? true : !!(covers.find((x) => x.line === li) || {}).perUnit
  )));

  const shares = candidates.map(({ campaign, covers }) => {
    const share = lines.map(() => 0);
    const value = Math.max(0, Number(campaign.discountValue) || 0);
    if (campaign.discountType === 'PERCENT') {
      const pct = Math.min(100, value);
      covers.forEach(({ line }) => {
        const perUnit = Math.round((lines[line].unitAgorot * pct) / 100 + 1e-7);
        share[line] = Math.min(perUnit * lines[line].quantity, lines[line].lineAgorot);
      });
      return share;
    }
    const valueAgorot = toAgorot(value);
    const pooled = [];
    covers.forEach(({ line, perUnit }) => {
      if (perUnit) share[line] = Math.min(valueAgorot, lines[line].unitAgorot) * lines[line].quantity;
      else pooled.push(line);
    });
    if (pooled.length > 0) {
      const covered = pooled.reduce((s, line) => s + lines[line].lineAgorot, 0);
      const spread = shareOut(Math.min(valueAgorot, covered), pooled.map((line) => lines[line].lineAgorot));
      pooled.forEach((line, k) => { share[line] = spread[k]; });
    }
    return share;
  });

  const taken = candidates.map(() => 0);
  const stackedLines = lines.map(() => false);
  const lineDiscounts = lines.map((l, li) => {
    let best = -1;
    const stack = [];
    candidates.forEach(({ campaign }, ci) => {
      const amount = shares[ci][li];
      if (!(amount > 0)) return;
      if (campaign.stackRule === 'STACK') stack.push(ci);
      else if (best < 0 || amount > shares[best][li]) best = ci;
    });
    const single = best >= 0 ? shares[best][li] : 0;
    const stacked = Math.min(stack.reduce((s, ci) => s + shares[ci][li], 0), l.lineAgorot);
    if (single === 0 && stacked === 0) return 0;
    const stackWins = stacked > single || (stacked === single && stack[0] < best);
    stackedLines[li] = stackWins;

    // What the winner takes off this line, candidate by candidate, capped at the line.
    const parts = [];
    let room = l.lineAgorot;
    (stackWins ? stack : [best]).forEach((ci) => {
      const part = Math.min(shares[ci][li], room);
      if (part <= 0) return;
      parts.push({ ci, amount: part });
      room -= part;
    });

    // The business's rounding, on the part that came off unit by unit only.
    const perUnitParts = parts.filter((p) => perUnitAt[p.ci][li]);
    const perUnitTotal = perUnitParts.reduce((s, p) => s + p.amount, 0);
    if (rounding !== 'off' && l.quantity > 0 && perUnitTotal > 0) {
      const wanted = l.lineAgorot
        - roundedUnitAgorot((l.lineAgorot - perUnitTotal) / l.quantity, l.unitAgorot, rounding) * l.quantity;
      // The odd agorot are one campaign's — the first that came off unit by
      // unit — so the contributions still add up to what came off the line.
      let move = wanted - perUnitTotal;
      perUnitParts.forEach((p) => {
        if (move === 0) return;
        const give = move > 0 ? move : Math.max(move, -p.amount);
        p.amount += give;
        move -= give;
      });
      // A rounding that asked for more than the line has leaves the rest behind.
      let over = parts.reduce((s, p) => s + p.amount, 0) - l.lineAgorot;
      for (let k = parts.length - 1; k >= 0 && over > 0; k--) {
        const back = Math.min(over, parts[k].amount);
        parts[k].amount -= back;
        over -= back;
      }
    }

    parts.forEach((p) => { taken[p.ci] += p.amount; });
    return parts.reduce((s, p) => s + p.amount, 0);
  });
  return { lineDiscounts, taken, stackedLines };
}

/**
 * WHAT A FREE-DELIVERY THRESHOLD IS MEASURED ON — the business's choice
 * (owner decision, CHK-23 / DISC-32g), `free-delivery-basis.ts` on the server.
 *
 * "Free delivery over ₪200" and a 15% sale: a ₪220 bracelet costs ₪187. After
 * discounts (the DEFAULT) that is ₪187, not over ₪200; before discounts it is the
 * ₪220 on the shelf. The shop page and the checkout quote carry the business's
 * `basis`; anything but `BEFORE_DISCOUNTS` — nothing at all included — is after.
 * Every threshold is read on it: the shop's own, a campaign's, a tier's "free
 * above". The desk's own discount on a placed order is not among the discounts.
 */
export const FREE_DELIVERY_BASES = ['AFTER_DISCOUNTS', 'BEFORE_DISCOUNTS'];

/** The basis a payload names, read the server's way: before discounts only when it says so exactly. */
export function freeDeliveryBasisOf(basis) {
  return basis === 'BEFORE_DISCOUNTS' ? 'BEFORE_DISCOUNTS' : 'AFTER_DISCOUNTS';
}

/** The amount a threshold is compared with: the list subtotal, less the discount after discounts. Whole agorot. */
export function freeDeliverySpend({ subtotal, discount = 0 } = {}, basis) {
  const sub = Math.max(0, toAgorot(subtotal));
  if (freeDeliveryBasisOf(basis) === 'BEFORE_DISCOUNTS') return sub / 100;
  return (sub - Math.min(sub, Math.max(0, toAgorot(discount)))) / 100;
}

/** Does `spend` reach `threshold`, to the agora? No threshold, no. */
export function reachesFreeDelivery(spend, threshold) {
  if (threshold === null || threshold === undefined || threshold === '') return false;
  const th = Number(threshold);
  return Number.isFinite(th) && toAgorot(spend) >= toAgorot(th);
}

/**
 * WHAT A BASKET COSTS ONCE THE CAMPAIGNS ARE COUNTED — the server's rule.
 *
 * `lines` are `[{ variant, product, quantity }]`, each variant at its LIST price
 * (its own price, else its main variant's default), or a booking line
 * `{ service: { id, price } }` / `{ package: { id, price } }`. Returns
 *   { subtotal, discount, total, applied, lines }
 * where `lines[i]` is `{ listPrice, quantity, subtotal, discount, total, stacked }`
 * and `applied` is `[{ campaign, discountAmount }]` for every campaign that took
 * something off, in the order the server names them. With `{ card: true }` the
 * lines are priced as ONE UNIT each is charged: a minimum spend counts only when
 * one unit already meets it (a ₪339 watch under "10% over ₪300" is 305.10 on
 * its card, as a basket of one is charged), and a once-per-order FIXED amount
 * (whole shop, category, collection) never does: a card is a unit, not an
 * order, and an order offer is shown beside its price (`campaignOrderOffers`),
 * never baked into it — ₪40 off a watches order is not a ₪30 strap for 0. With
 * `{ withAppointments: true }` campaigns tied to an appointment's date are kept:
 * the caller has already matched them to one.
 *
 * A basket (never a card) also says what it earns towards FREE DELIVERY, as
 * `applyCampaigns` reports it: `freeDeliveryThreshold`, the smallest threshold
 * of the free-delivery offers it passes the gates of (dates, targets, minimum
 * spend, cap), or null; `freeDeliverySpend`, the amount a threshold is measured
 * on for `{ freeDeliveryBasis }` (after discounts unless it says before); and
 * `freeDeliveryReached`. DISPLAY ONLY — the checkout and the booking recompute
 * all of it server-side.
 */
export function campaignBasketPricing(lines, campaigns, { card = false, withAppointments = false, freeDeliveryBasis = null } = {}) {
  const priced = (lines || []).map((l) => {
    const bookingItem = l?.service || l?.package || null;
    const kind = l?.service ? 'SERVICE' : l?.package ? 'PACKAGE' : 'PRODUCT';
    const variant = l?.variant || {};
    const listPrice = bookingItem
      ? Number(bookingItem.price) || 0
      : Number(variant.price ?? variant.mainVariant?.defaultPrice ?? variant.defaultPrice ?? 0) || 0;
    const quantity = Number(l?.quantity) > 0 ? Math.floor(Number(l.quantity)) : 1;
    const unitAgorot = toAgorot(listPrice);
    return {
      kind,
      itemId: bookingItem ? (bookingItem.id ?? bookingItem._id) : null,
      variant,
      product: l?.product || {},
      listPrice,
      quantity,
      unitAgorot,
      lineAgorot: unitAgorot * quantity,
    };
  });
  const subtotalAgorot = priced.reduce((s, l) => s + l.lineAgorot, 0);
  // What a minimum spend is measured on: the basket, or — for a card — one unit.
  const minimumSpendAgorot = card ? priced.reduce((s, l) => s + l.unitAgorot, 0) : subtotalAgorot;

  const candidates = [];
  let freeDeliveryThreshold = null;
  (campaigns || [])
    .map((campaign, index) => ({ campaign, index }))
    .filter(({ campaign }) => campaignIsInPlay(campaign, withAppointments))
    .sort(byPriorityThenAge)
    .forEach(({ campaign }) => {
      const minSpend = campaignMinimumSpend(campaign);
      if (minSpend > 0 && minimumSpendAgorot < toAgorot(minSpend)) return;
      // Free delivery through the same gates as a discount, on a basket that
      // has a line the offer reaches; the smallest threshold wins.
      const threshold = campaign.freeDeliveryThreshold;
      if (!card && threshold !== null && threshold !== undefined && threshold !== '' && Number.isFinite(Number(threshold))) {
        const reaches = priced.some((l) => (l.kind === 'PRODUCT'
          ? campaignAppliesToProduct(campaign, l.product, l.variant)
          : campaignAppliesToBookingItem(campaign, l.kind, l.itemId)));
        if (reaches && (freeDeliveryThreshold === null || Number(threshold) < freeDeliveryThreshold)) {
          freeDeliveryThreshold = Number(threshold);
        }
      }
      if (campaignIsFreeDeliveryOnly(campaign)) return;
      const covers = [];
      priced.forEach((l, line) => {
        const how = l.kind === 'PRODUCT'
          ? campaignAppliesToProduct(campaign, l.product, l.variant)
          : campaignAppliesToBookingItem(campaign, l.kind, l.itemId);
        if (!how) return;
        // A booking line is booked one at a time; only a product card is a unit of an order.
        if (card && l.kind === 'PRODUCT' && isOrderAmount(campaign, how)) return;
        covers.push({ line, perUnit: how.perUnit });
      });
      if (covers.length > 0) candidates.push({ campaign, covers });
    });

  // The business's rounding of a discounted price rides in on the campaigns
  // themselves (`priceRounding`), as the shop's day does — so every surface
  // that prices from this payload lands on the number the card shows.
  const { lineDiscounts, taken, stackedLines } = combineCampaignShares(priced, candidates, priceRoundingOf(campaigns));
  const discountAgorot = lineDiscounts.reduce((s, a) => s + a, 0);
  const basis = freeDeliveryBasisOf(freeDeliveryBasis);
  const spend = freeDeliverySpend({ subtotal: subtotalAgorot / 100, discount: discountAgorot / 100 }, basis);
  return {
    subtotal: subtotalAgorot / 100,
    discount: discountAgorot / 100,
    total: (subtotalAgorot - discountAgorot) / 100,
    freeDeliveryThreshold,
    freeDeliveryBasis: basis,
    freeDeliverySpend: spend,
    freeDeliveryReached: reachesFreeDelivery(spend, freeDeliveryThreshold),
    applied: candidates
      .map(({ campaign }, ci) => ({ campaign, discountAmount: taken[ci] / 100 }))
      .filter((a) => a.discountAmount > 0),
    lines: priced.map((l, i) => ({
      listPrice: l.listPrice,
      quantity: l.quantity,
      subtotal: l.lineAgorot / 100,
      discount: lineDiscounts[i] / 100,
      total: (l.lineAgorot - lineDiscounts[i]) / 100,
      stacked: stackedLines[i],
    })),
  };
}

/**
 * WHAT A LINE COSTS ONCE THE CAMPAIGNS ARE COUNTED — the one display rule.
 *
 * `variant` at its LIST price, `quantity` units of it, as a one-line basket
 * priced for a card (a minimum spend counts only when one unit meets it). Returns
 *   { listPrice, quantity, subtotal, discount, total, applied, stacked }
 * where `applied` is `[{ campaign, discountAmount }]` in the order the server
 * reports them. DISPLAY ONLY — the checkout recomputes all of it server-side.
 */
export function campaignLinePricing(variant, product, campaigns, { quantity = 1 } = {}) {
  const basket = campaignBasketPricing([{ variant, product, quantity }], campaigns, { card: true });
  const line = basket.lines[0];
  return {
    listPrice: line.listPrice,
    quantity: line.quantity,
    subtotal: line.subtotal,
    discount: line.discount,
    total: line.total,
    applied: basket.applied,
    stacked: line.stacked,
  };
}

/**
 * THE OFFERS A PRODUCT TAKES PART IN THAT ITS CARD PRICE DOES NOT SHOW — for
 * the note beside its price.
 *
 * A card is priced for one unit (`campaignLinePricing`), so two kinds of live
 * offer reach a product without being in that price:
 *   - a once-per-order FIXED amount on the whole shop, a category or a
 *     collection ("₪40 הנחה על הזמנת שעונים") — `{ campaign, amount }`;
 *   - anything that waits on a MINIMUM SPEND one unit does not reach — `minSpend`
 *     says how much, with `amount` (a fixed sum) or `percent`, and `perUnit: true`
 *     when it comes off each unit of this item rather than once per order. An
 *     offer one unit already qualifies for is in the card's price instead, unless
 *     it is a once-per-order amount, which is named here with its minimum.
 * Every such offer is returned, in tie-break order — not only the first — as
 * long as it could take more off this item than its card price already does.
 *
 * `joins` says how an offer relates to what comes before it (the card's own
 * discount, then the offers listed above it), by the checkout's rule that a
 * line gets the BIGGER of the best "Only one" and all its "Allow stacking"
 * added up: 'plus' only when it and everything before it stack; otherwise 'or',
 * because the checkout picks one. Absent for the first, with nothing before it.
 *
 * And a card a campaign took to nothing (`discountMadeItFree`) is FREE: then the
 * only entry is `{ campaign, campaigns, free: true, was }` — the campaigns that
 * made it so and the price it was — since nothing more can come off it. A
 * per-unit offer over a minimum that would make it free carries `free: true`.
 */
export function campaignOrderOffers(variant, product, campaigns) {
  const v = variant || {};
  const p = product || {};
  const all = campaigns || [];
  const oneUnit = (list) => campaignBasketPricing([{ variant: v, product: p, quantity: 1 }], list, { card: true });
  const card = oneUnit(all);
  const line = card.lines[0];
  if (line.discount > 0 && discountMadeItFree(line.total, line.listPrice)) {
    const makers = card.applied.map((a) => a.campaign);
    return [{ campaign: makers[0], campaigns: makers, free: true, was: line.listPrice }];
  }

  const offers = [];
  let everythingBeforeStacks = line.discount === 0 || line.stacked;
  let anythingBefore = line.discount > 0;
  all
    .map((campaign, index) => ({ campaign, index }))
    .filter(({ campaign }) => campaignIsLive(campaign) && Number(campaign.discountValue) > 0)
    .sort(byPriorityThenAge)
    .forEach(({ campaign }) => {
      const how = campaignAppliesToProduct(campaign, p, v);
      if (!how) return;
      const minSpend = campaignMinimumSpend(campaign);
      const orderAmount = isOrderAmount(campaign, how);
      // Per unit with no minimum: already in the card's price.
      if (!orderAmount && minSpend === 0) return;
      const stacks = campaign.stackRule === 'STACK' && everythingBeforeStacks;
      const entry = { campaign };
      if (orderAmount) {
        const amount = Number(campaign.discountValue);
        // Alone in a basket, it takes at most the item's price; one that can
        // never beat the card's own discount, and does not add to it, is noise.
        if (!stacks && !(Math.min(amount, line.listPrice) > line.discount)) return;
        entry.amount = amount;
      } else {
        // What this item would cost with the minimum met.
        const unlocked = oneUnit(all.map((c) => (c === campaign ? { ...c, minSpend: null, minimumSpend: null } : c))).lines[0];
        if (!(unlocked.discount > line.discount)) return;
        entry.perUnit = true;
        if (campaign.discountType === 'PERCENT') entry.percent = Math.min(100, Number(campaign.discountValue));
        else entry.amount = Number(campaign.discountValue);
        if (discountMadeItFree(unlocked.total, unlocked.listPrice)) entry.free = true;
      }
      if (minSpend > 0) entry.minSpend = minSpend;
      if (anythingBefore) entry.joins = stacks ? 'plus' : 'or';
      if (campaign.stackRule !== 'STACK') everythingBeforeStacks = false;
      anythingBefore = true;
      offers.push(entry);
    });
  return offers;
}

/**
 * Effective display price for a variant given the active campaigns. DISPLAY
 * ONLY — checkout recomputes the discount server-side from the campaign and the
 * stored variant.price, so showing this here never double-discounts.
 *
 * The campaign discount comes from `campaignLinePricing` (one unit), measured
 * against the stored price; the manual compare-at "sale"
 * (variant.compareAtPrice), when there is one, stays the price to strike
 * through. Returns { price, compareAt, discounted, pct } — for the list price,
 * the discount and which campaigns gave it, ask `campaignLinePricing`.
 */
export function effectiveVariantPrice(variant, product, campaigns) {
  // Inherit-priced variants (price=null on a raw/recommendation shape) fall
  // back to the main variant's defaultPrice so we never display ₪0. Serialized
  // storefront products already arrive with an effective non-null price.
  const pricing = campaignLinePricing(variant, product, campaigns, { quantity: 1 });
  const current = pricing.listPrice;
  const rawCompareVal = variant?.compareAtPrice ?? variant?.mainVariant?.defaultCompareAtPrice;
  const rawCompare = rawCompareVal != null ? Number(rawCompareVal) : null;
  const manualCompareAt = rawCompare != null && rawCompare > current ? rawCompare : null;

  const price = pricing.total;
  const candidate = manualCompareAt != null ? manualCompareAt : price < current ? current : null;
  // `discountPercent` is the single arbiter of whether there is a discount to
  // show at all — see its note. A campaign that took the price to nothing, and
  // a compare-at standing against a price of nothing, both land here and both
  // come back null, so no surface downstream has to remember to check.
  const pct = discountPercent(price, candidate);
  const discounted = pct != null;
  return { price, compareAt: discounted ? candidate : null, discounted, pct: pct ?? 0 };
}

/**
 * WHAT CAME OFF ONE LINE OF A BOOKING, AS THE SERVER PRICED IT.
 *
 * The better deal wins LINE BY LINE (owner decision 2026-09-13), so two lines of
 * one booking can take two different campaigns: a line's discount can be neither
 * derived from the first campaign the server names nor spread over the lines by
 * price. `applyCampaigns` returns each line's own figure in
 * `lineItems[].discount`, in the order the lines were sent, and this reads it.
 * The same service booked twice is two lines; `occurrence` says which of them
 * (0 = the first).
 *
 * Returns the amount, or null when the server did not price that line or did
 * not say (an older server). DISPLAY ONLY, on the storefront and at the desk.
 */
export function campaignDiscountOnBookingLine(result, itemType, itemId, occurrence = 0) {
  const lines = Array.isArray(result?.lineItems) ? result.lineItems : [];
  const nth = Math.max(0, Math.floor(Number(occurrence) || 0));
  const line = lines.filter((l) => l?.itemType === itemType && String(l?.itemId) === String(itemId))[nth];
  if (!line || line.discount === null || line.discount === undefined || line.discount === '') return null;
  const off = Number(line.discount);
  return Number.isFinite(off) ? Math.max(0, off) : null;
}

export function filterShopTopCampaigns(campaigns) {
  // What the "Discounts & offers" row may advertise: pinned by the owner, live
  // TODAY by the same window the price uses (an offer that has not started is
  // not one the checkout will honour), able to reach a product at all, and not
  // an appointment-only campaign.
  const showInTop = (c) => c.showInTop === true || c.showAtTop === true || c.pinned === true;
  return (campaigns || []).filter(
    (c) => showInTop(c)
      && campaignBookingDateInRange(c)
      && !campaignNeedsAnAppointment(c)
      && SHOP_PROMOTION_TARGETS.has(c.targetType),
  );
}

/**
 * A PRODUCT'S "FROM" PRICE ON A SMALL CARD — its cheapest variant once the
 * campaigns are counted, through `effectiveVariantPrice` and nothing else.
 *
 * For the strips and the recommendation cards, whose `+` opens the option
 * picker on a multi-variant product, so leading with the cheapest never
 * undercuts what is charged. Variants with no resolvable price are skipped (a
 * price-less option must not drag the card to ₪0); a serialized card with no
 * variant prices at all falls back to its `priceFrom`, discounted the same way.
 * A "from" names a price the shopper can PAY: the rows a shop counts and has
 * none of left are read only when no row can be bought (D1-24 review, M1 - a
 * sold-out Kinder at 65 said «من 65» while only 70 could be bought), and
 * `priceVaries` is read over the same rows.
 * Returns `{ variant, price (number|null), compareAt, discounted, pct,
 * priceVaries }`.
 */
export function cheapestCampaignPrice(product, campaigns) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const priced = variants.filter((v) => variantOwnPrice(v) != null);
  if (priced.length === 0) {
    const from = product?.priceFrom != null && product.priceFrom !== '' ? Number(product.priceFrom) : NaN;
    if (!Number.isFinite(from)) {
      return { variant: variants[0] || null, price: null, compareAt: null, discounted: false, pct: 0, priceVaries: false };
    }
    const to = product?.priceTo != null && product.priceTo !== '' ? Number(product.priceTo) : null;
    const info = effectiveVariantPrice({ ...(variants[0] || {}), price: from }, product, campaigns);
    return { variant: variants[0] || null, ...info, priceVaries: to != null && to !== from };
  }
  const buyable = priced.filter((v) => variantPurchaseCap(product, v) !== 0);
  let best = null;
  const prices = [];
  for (const v of buyable.length ? buyable : priced) {
    const info = effectiveVariantPrice(v, product, campaigns);
    prices.push(info.price);
    if (best == null || info.price < best.price) best = { variant: v, ...info };
  }
  const priceVaries = prices.some((p) => Math.abs(p - prices[0]) > 1e-9);
  return { ...best, priceVaries };
}

/**
 * WHAT A SERVICE OR PACKAGE COSTS ONCE THE CAMPAIGNS ARE COUNTED — for a card.
 *
 * The same rule the booking is charged by (`campaignBasketPricing`, the
 * server's `applyCampaigns`): the bigger of the best single "Only one" and all
 * the "Allow stacking" discounts added up, whole agorot. `kind` is 'SERVICE' or
 * 'PACKAGE'. Returns { listPrice, discount, total, applied, stacked }.
 */
export function campaignBookingItemPricing(item, kind, campaigns, { withAppointments = false } = {}) {
  const line = kind === 'PACKAGE' ? { package: item } : { service: item };
  const basket = campaignBasketPricing([line], campaigns, { card: true, withAppointments });
  const priced = basket.lines[0];
  return {
    listPrice: priced.listPrice,
    discount: priced.discount,
    total: priced.total,
    applied: basket.applied,
    stacked: priced.stacked,
  };
}

/**
 * A SERVICE CARD'S STRUCK-THROUGH PRICE, OR NULL.
 *
 * Over the campaigns the page advertises: the `guaranteed` ones (no appointment
 * date, no minimum spend) first; only when none of them takes anything off, the
 * `dateRestricted` ones too, drawn with an asterisk because the appointment's
 * date decides. Returns { after, asterisk, applied } or null when nothing comes
 * off — and null for a hidden, "from" or free price, which has no card discount.
 */
export function campaignBookingItemPreview(item, kind, { guaranteed = [], dateRestricted = [] } = {}) {
  const price = Number(item?.price ?? 0);
  // A template literal on purpose: the portal's storefront-parity crawl reads
  // any quote after the word as an import specifier.
  if (!(price > 0) || item?.hidePrice === true || item?.priceType === `from`) return null;
  const sure = campaignBookingItemPricing(item, kind, guaranteed);
  if (sure.discount > 0) return { after: sure.total, asterisk: false, applied: sure.applied };
  if (!dateRestricted.length) return null;
  const maybe = campaignBookingItemPricing(item, kind, [...guaranteed, ...dateRestricted], { withAppointments: true });
  if (maybe.discount > 0) return { after: maybe.total, asterisk: true, applied: maybe.applied };
  return null;
}

/**
 * A BOOKING CART'S ROWS, EACH WITH WHAT THE ENGINE TOOK OFF IT (SWP-P-21).
 *
 * `services` then `packages`, in the order a booking step draws them and sends
 * them to `applyCampaigns`; each row gets `campaignDiscountOnBookingLine` for
 * its own line, the same service twice taking each of its lines in turn. A row
 * the server has not priced reads 0, so no discount is drawn under it — never a
 * share of the booking's discount.
 */
export function campaignDiscountsOnBookingCart(result, { services = [], packages = [] } = {}) {
  const idOf = (row) => (typeof row === 'string'
    ? row
    : (row?.serviceId ?? row?.packageId ?? row?._id ?? row?.id));
  const rows = [
    ...(services || []).map((row) => ['SERVICE', idOf(row)]),
    ...(packages || []).map((row) => ['PACKAGE', idOf(row)]),
  ];
  const seen = new Map();
  return rows.map(([type, id]) => {
    if (id == null || id === '') return 0;
    const key = `${type}:${id}`;
    const nth = seen.get(key) || 0;
    seen.set(key, nth + 1);
    return campaignDiscountOnBookingLine(result, type, id, nth) ?? 0;
  });
}

/**
 * THE CAMPAIGNS A SERVICE CARD IS PRICED WITH ON A BOOKING SURFACE (SWP-P-42).
 *
 * Every campaign the booking will be charged with — not only the ones the owner
 * chose to "Show in top", which is a question of where an offer is ADVERTISED,
 * not of what it takes off. Gated by `campaignIsLive`, in the shop's own day.
 * Returns `{ guaranteed, dateRestricted }` for `campaignBookingItemPreview`: the
 * appointment promotions apart, because the appointment's date decides them.
 */
export function bookingCardCampaigns(campaigns) {
  const guaranteed = [];
  const dateRestricted = [];
  (campaigns || []).forEach((c) => {
    if (!campaignIsLive(c, true)) return;
    if (campaignNeedsAnAppointment(c)) dateRestricted.push(c);
    else guaranteed.push(c);
  });
  return { guaranteed, dateRestricted };
}

/**
 * THE OFFERS A BOOKING STEP ADVERTISES (SWP-P-42): pinned by the owner ("Show in
 * top"), open today IN THE SHOP'S OWN DAY — never the shopper's device day — and
 * not used up by this customer.
 */
export function bookingTopOffers(campaigns) {
  const pinned = (c) => c.showInTop === true || c.showAtTop === true || c.pinned === true;
  return (campaigns || []).filter((c) => !!c
    && pinned(c)
    && !(c.maxUsesPerCustomer != null && c.remainingUses === 0)
    && campaignBookingDateInRange(c));
}

/** Today as `YYYY-MM-DD` in the shop's own calendar, as the campaign carries it (its zone, else the published day, else UTC). */
export function campaignShopTodayKey(campaign) {
  return shopTodayKey(campaign);
}

/** Has this campaign's booking window closed, by the shop's own day? No end never closes. */
export function campaignBookingWindowEnded(campaign) {
  const end = utcDayKey(campaign?.bookingDateEnd ?? campaign?.booking_date_end);
  return !!end && shopTodayKey(campaign) > end;
}

/** Minutes past midnight right now on the shop's clock (UTC when the campaign names no zone). */
function shopMinutesIntoDay(campaign) {
  const now = new Date();
  const zone = campaign && campaign.timeZone;
  if (zone) {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(now);
      const part = (type) => Number((parts.find((p) => p.type === type) || {}).value);
      const minutes = (part('hour') % 24) * 60 + part('minute');
      if (Number.isFinite(minutes)) return minutes;
    } catch (_) { /* a zone this browser does not know: fall through */ }
  }
  return now.getUTCHours() * 60 + now.getUTCMinutes();
}

/**
 * HOW LONG IS LEFT TO BOOK, COUNTED IN THE SHOP'S OWN DAYS.
 *
 * `{ type: 'days', value }` when the window ends one or two shop days after
 * today, `{ type: 'hours', value }` (at least 1) on its last day, until the
 * shop's midnight; null for no end, an ended window, or three days or more.
 */
export function campaignBookingTimeLeft(campaign) {
  const end = utcDayKey(campaign?.bookingDateEnd ?? campaign?.booking_date_end);
  if (!end) return null;
  const today = shopTodayKey(campaign);
  const days = Math.round((Date.parse(`${end}T00:00:00.000Z`) - Date.parse(`${today}T00:00:00.000Z`)) / 86400000);
  if (!Number.isFinite(days) || days < 0 || days >= 3) return null;
  if (days >= 1) return { type: 'days', value: days };
  return { type: 'hours', value: Math.max(1, Math.ceil((24 * 60 - shopMinutesIntoDay(campaign)) / 60)) };
}

/** An appointment's calendar day and weekday in the shop's zone, or null when there is no date. */
function appointmentDayInShop(campaign, { startsAt, dateKey } = {}) {
  if (startsAt) {
    const instant = new Date(startsAt);
    if (Number.isFinite(instant.getTime())) {
      const zone = (campaign && campaign.timeZone) || 'UTC';
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
        }).formatToParts(instant);
        const part = (type) => (parts.find((p) => p.type === type) || {}).value;
        const day = `${part('year')}-${part('month')}-${part('day')}`;
        const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(part('weekday'));
        if (DAY_KEY.test(day) && weekday >= 0) return { day, weekday };
      } catch (_) { /* a zone this browser does not know: fall through */ }
      return { day: instant.toISOString().slice(0, 10), weekday: instant.getUTCDay() };
    }
  }
  if (typeof dateKey === 'string' && DAY_KEY.test(dateKey)) {
    return { day: dateKey, weekday: new Date(`${dateKey}T00:00:00.000Z`).getUTCDay() };
  }
  return null;
}

/**
 * THE CAMPAIGNS THAT CAN REACH A BOOKING AT `startsAt` (or on `dateKey`), gated as
 * the server gates them (SWP-P-42): live today in the shop's day, and — for an
 * appointment promotion — the appointment's day and weekday, IN THE SHOP'S ZONE,
 * inside its window. Never the UTC date of the start, never the device's
 * `getDay()`: an appointment at 00:30 on a Monday in Israel is a Monday.
 * With no appointment date an appointment promotion is kept, as before: the
 * caller prices with `withAppointments` only once a date is in hand.
 */
export function campaignsForAppointment(campaigns, appointment = {}) {
  return (campaigns || []).filter((c) => {
    if (!campaignIsLive(c, true)) return false;
    if (!campaignNeedsAnAppointment(c)) return true;
    const when = appointmentDayInShop(c, appointment);
    if (!when) return true;
    const start = utcDayKey(c.appointmentDateStart);
    const end = utcDayKey(c.appointmentDateEnd);
    if (start && when.day < start) return false;
    if (end && when.day > end) return false;
    const days = c.appointmentDaysOfWeek;
    if (Array.isArray(days) && days.length > 0 && !days.map(Number).includes(when.weekday)) return false;
    return true;
  });
}

/**
 * THE FREE-DELIVERY PILL'S LABEL, IN THE READER'S LANGUAGE.
 *
 * This used to read `freeDeliveryPromo.defaultLabel || t(...)`, and the server
 * ALWAYS sends `defaultLabel` — the payload is
 * `{ threshold, labelKey, defaultLabel }` — so the `t()` branch was dead code
 * and the Hebrew and Arabic strings in the locale files were unreachable. The
 * pill rendered English on an Arabic shop and on a Hebrew one whatever the
 * reader chose.
 *
 * The order is the one `utils/shopFeatures.js` already applies to the same
 * kind of row, and it is deliberate at every step:
 *
 *   1. THE OWNER'S OWN WORDS, in the reader's language. A manual claim is the
 *      truth for that row and our translation must never overwrite it. Text
 *      typed only in Hebrew does NOT mask an Arabic reader, though — for a
 *      language the owner has not written, our translation is the better
 *      answer than someone else's alphabet.
 *   2. OUR TRANSLATION, by the key the SERVER names (`labelKey`), not by a key
 *      typed here. The server owns which sentence this row is.
 *   3. THE SERVER'S `defaultLabel`, for a key we have not translated yet, so a
 *      new claim reads as a sentence rather than as `shop.somethingNew`.
 *   4. English, so nothing can render a bare key.
 *
 * THE THRESHOLD IS MONEY, AND MONEY HAS A SIDE. `200 ₪` in Arabic and Hebrew,
 * `₪200` in English. The locale strings already place the symbol; the
 * interpolated value is the AMOUNT, formatted for this shop's own currency, so
 * a JOD shop gets three decimals and the right symbol without a second copy of
 * the rule. Passing the bare number worked only because every shop was ILS.
 */
export function mergeShopTopPromotions(campaigns, freeDeliveryPromo, t, opts = {}) {
  const list = filterShopTopCampaigns(campaigns);
  if (freeDeliveryPromo?.threshold > 0) {
    const threshold = freeDeliveryPromo.threshold;
    const language = opts.language;
    const currency = freeDeliveryPromo.currency || opts.currency || 'ILS';
    /* THE MONEY, WITH ITS SYMBOL ON THE SIDE THE LANGUAGE PUTS IT.
     *
     * `200 ₪` in Arabic and Hebrew, `₪200` in English — and the SYMBOL comes
     * from this shop's currency, so a Jordanian shop is not labelled in
     * shekels. The three locale strings interpolate this whole string and
     * carry no symbol of their own; they used to hardcode `₪`, which was
     * invisible while every shop was Israeli.
     *
     * Whole units when the threshold IS whole (`200 ₪`, as the seeded label
     * writes it, never `200.00 ₪`), and its agorot when it is not: 199.90 is
     * the number the checkout compares a basket with, and a pill that rounded
     * it to 200 promised free delivery on the wrong side of it (SWP-P-43). */
    const rtlMoney = ['ar', 'he'].includes(String(language || '').toLowerCase().split('-')[0]);
    const number = shopAmount(threshold, currency);
    const symbol = currencySymbol(currency, language);
    const amount = rtlMoney ? `${number} ${symbol}` : `${symbol}${number}`;
    const owner = ownerText(freeDeliveryPromo.text, language);
    const key = freeDeliveryPromo.labelKey || 'shop.freeDeliveryOver';
    const fallback = freeDeliveryPromo.defaultLabel
      || `Free delivery on orders over ${amount}`;
    list.unshift({
      id: '__free_delivery_promo__',
      name: owner
        || (t ? t(key, { threshold: amount, defaultValue: fallback }) : fallback),
      description: freeDeliveryPromo.description || null,
      discountType: 'PERCENT',
      discountValue: 100,
      isFreeDeliveryPromo: true,
      showInTop: true,
    });
  }
  return list;
}
